
import * as Actions from '@/redux/actions';
import * as Constants from '@/redux/constants';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useDialogs } from '@toolpad/core';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import snakeCase from 'lodash/snakeCase';
import toUpper from 'lodash/toUpper';
import plur from 'plur';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Button } from 'components/button';
import { SearchInput } from 'components/input';
import Table from 'components/table';
import TableRow from 'components/table/TableRow';
import { useDispatch, useLanguageNavigate, useListParams, useSelector } from 'hooks';
import { uncapitalize } from 'lib/utils';
import { useI18n } from 'locales';
import * as Models from 'models';
import DialogDelete from './DialogDelete';
import DialogEdit from './DialogEdit';
import DialogView from './DialogView';
import FilterMenu from './FilterMenu';

const modelsWithDedicatedPages = [];

function GeneralListLayout({ model, tabs, headCells, payload = {}, defaultListParams = {}, title, fullHeight, forceReload, onView, onCreate, onEdit, onFetching }) {
  const tableRef = useRef();
  const listParams = useListParams(defaultListParams);
  const { currentInfo, filtersInfo, itemsInfo, itemInfo, itemSendInfo, optionsItemInfo } = useSelector((state) => ({
    currentInfo: state.currentInfo,
    filtersInfo: get(state, `filters${model}Info`),
    itemsInfo: get(state, `${plur(uncapitalize(model))}Info`),
    itemInfo: get(state, `${uncapitalize(model)}Info`),
    itemSendInfo: get(state, `${uncapitalize(model)}SendInfo`),
    optionsItemInfo: get(state, `options${model}Info`),
  }), shallowEqual);
  const t = useI18n();
  const dispatch = useDispatch();
  const navigate = useLanguageNavigate();
  const dialogs = useDialogs();
  const [localSearchKey, setLocalSearchKey] = useState(listParams.getSearch() ?? '');
  const [tableHeight, setTableHeight] = useState(0);
  const [dialogViewId, setDialogViewId] = useState(false);
  const [dialogEditId, setDialogEditId] = useState(false);
  const [dialogDeleteId, setDialogDeleteId] = useState(false);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const isFetching = itemsInfo.isFetching || itemInfo.isFetching;

  if (!headCells) {
    headCells = Models[model].headCells;
  }

  const hasDedicatedPages = useMemo(() => modelsWithDedicatedPages.includes(model), [model]);
  const permissions = useMemo(() => {
    const ret = {};

    const tmp = model.toLowerCase();
    const permissions = get(currentInfo, 'data.permissions');

    for (const action of ['create', 'read', 'update', 'delete']) {
      ret[action] = !!(permissions && permissions[`${tmp}.${action}`]);
    }

    if (model === 'User') {
      // ret.create = false;
      // ret.delete = false;
    } else if (model.indexOf('User') !== -1) {
      ret.create = false;
      ret.update = false;
      ret.delete = false;
    }

    return ret;
  }, [currentInfo, model]);

  useEffect(() => { (onFetching) && onFetching(isFetching); }, [isFetching]);

  useEffect(() => {
    if (Actions[`readFilters${model}`]) {
      dispatch(Actions[`readFilters${model}`]());
    }
  }, [dispatch, model]);

  useEffect(() => {
    if (tabs) {
      const tab = tabs[currentTabIndex];
      listParams.setFilters(listParams.getFilters().merge(tab.value));
    }
  }, [currentTabIndex, tabs, listParams]);

  const fetchData = useCallback(() => {
    dispatch(Actions[`read${plur(model)}`]({
      payload: {
        ...listParams.data,
        ...payload,
        filters: {
          ...listParams.data.filters,
          ...payload.filters,
        },
      },
    }));
  }, [dispatch, model, listParams.data]);

  useEffect(fetchData, [fetchData]);

  useEffect(() => {
    if (forceReload) {
      fetchData();
    }
  }, [forceReload, fetchData]);

  useEffect(() => {
    const con = toUpper(snakeCase(model));
    switch (itemInfo.type) {
      case Constants[`CREATE_${con}`].SUCCESS:
      case Constants[`UPDATE_${con}`].SUCCESS:
      case Constants[`DELETE_${con}`].SUCCESS:
        fetchData();
        break;
      case Constants.CANCEL_SUBSCRIPTION.SUCCESS:
        dialogs.alert(t('The subscription was successfully cancelled.'), { title: t('Subscription Cancelled'), okText: t('OK') })
          .then(() => {
            dispatch(Actions.clearCancelSubscription());
            fetchData();
          });
        break;
    }
  }, [itemInfo.type, model]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (itemSendInfo && itemSendInfo.type) {
      const onFulfilled = () => {
        Actions[`clearSend${model}`] && dispatch(Actions[`clearSend${model}`]());
      };
      switch (itemSendInfo.type) {
        case Constants[`SEND_${toUpper(snakeCase(model))}`].SUCCESS:
          dialogs.alert(`The ${model} is sent to the customer successfully.`, { title: `${capitalize(model)} Sent`, okText: 'OK' }).then(onFulfilled);
          fetchData();
          break;
        case Constants[`SEND_${toUpper(snakeCase(model))}`].FAILURE:
          dialogs.alert(get(itemSendInfo, 'error.message') || `Failed to send the ${model} to the customer.`, { title: 'Failed to Send ' + capitalize(model), okText: 'OK' }).then(onFulfilled);
          break;
      }
    }
  }, [itemSendInfo, model]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeLocalSearchKeyHandler = useCallback((e) => setLocalSearchKey(e.target.value), [setLocalSearchKey]);

  const onChangeSearchHandler = useCallback(() => {
    listParams.setSearch(localSearchKey);
  }, [listParams.setSearch, localSearchKey]);

  const onChangeFilterHandler = useCallback((filterKey, value) => {
    if (value !== '' && value !== null && value !== undefined) {
      listParams.setFilters(listParams.getFilters().set(filterKey, value));
    } else {
      listParams.setFilters(listParams.getFilters().delete(filterKey));
    }
  }, [listParams]);

  const onClearFilterHandler = useCallback(() => {
    listParams.setFilters(listParams.getFilters().clear());
  }, [listParams]);

  const onClickViewHandler = useCallback((row) => {
    if (!onView) return;

    if (onView) {
      return onView(row);
    }
    if (modelsWithDedicatedPages.includes(model)) {
      return navigate(`/${uncapitalize(model)}/view/?id=${row.id}`);
    }
    return setDialogViewId(row.id);
  }, [onView, model, navigate]);
  const onClickCreateHandler = useCallback(() => {
    if (onCreate) {
      return onCreate();
    }
    if (modelsWithDedicatedPages.includes(model)) {
      return navigate(`/${uncapitalize(model)}/edit`);
    }
    return setDialogEditId(null);
  }, [onCreate, model, navigate]);
  const onClickEditHandler = useCallback((row) => {
    if (onEdit) {
      return onEdit(row);
    }
    if (modelsWithDedicatedPages.includes(model)) {
      return navigate(`/${uncapitalize(model)}/edit/?id=${row.id}`);
    }
    return setDialogEditId(row.id);
  }, [onEdit, model, navigate]);
  const onClickDeleteHandler = useCallback((row) => setDialogDeleteId(row.id), []);
  const onCloseDialogViewHandler = useCallback(() => setDialogViewId(false), []);
  const onCloseDialogEditHandler = useCallback(() => setDialogEditId(false), []);
  const onCloseDialogDeleteHandler = useCallback(() => setDialogDeleteId(false), []);
  const onChangeTabHandler = useCallback((e, v) => setCurrentTabIndex(v), []);
  const onClickUnsubscribeHandler = useCallback(async (row) => {
    const confirmed = await dialogs.confirm(
      t('Are you sure you want to unsubscribe this PCG instruction?'),
      {
        title: t('Unsubscribe'),
        okText: t('Unsubscribe'),
        cancelText: t('Do Not Unsubscribe'),
        severity: 'error',
      },
    );

    if (confirmed) {
      dispatch(Actions.cancelSubscription({ payload: { id: row.id } }));
    }
  }, [dispatch, dialogs, t]);


  const renderTab = useCallback((tab, index) => (
    <Tab
      key={`general-list-layout-tab-${index}`}
      label={tab.label}
      icon={
        tab.count !== undefined && tab.count !== null && (
          <Box sx={{ pl: 1 }}>
            <Badge badgeContent={`${tab.count}`} color='primary' />
          </Box>
        )
      }
      iconPosition='end'
      sx={{
        maxWidth: '10rem',
      }}
      wrapped
    />
  ), []);
  const renderRow = useCallback(({ isItemSelected, labelId, row, handleClick }, index) => {
    return (
      <TableRow
        key={`table-row-${index}`}
        headCells={headCells}
        row={row}
        isItemSelected={isItemSelected}
        labelId={labelId}
        dispatch={dispatch}
        onSelect={handleClick}
        onClickView={onClickViewHandler}
        onClickEdit={permissions.update ? onClickEditHandler : undefined}
        onClickDelete={permissions.delete ? onClickDeleteHandler : undefined}
        onClickUnsubscribe={onClickUnsubscribeHandler}
      />
    );
  }, [model, permissions, onClickViewHandler, onClickEditHandler, onClickDeleteHandler, onClickUnsubscribeHandler, headCells, dispatch]);

  useEffect(() => {
    const onResizeHandler = () => {
      const boundingClientRect = tableRef.current?.getBoundingClientRect();
      if (boundingClientRect) {
        const oneRem = parseInt(getComputedStyle(document.documentElement).fontSize?.replace('px', '')) || 16;
        setTableHeight(Math.max(300, window.innerHeight - (fullHeight ? 0 : boundingClientRect.y) - oneRem * 2));
      }
    };

    window.addEventListener('resize', onResizeHandler);
    onResizeHandler();

    return () => window.removeEventListener('resize', onResizeHandler);
  }, [tableRef.current, fullHeight]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Stack direction='column' gap={2}>
        <Grid container spacing={2} alignItems={isMobile ? 'flex-start' : 'center'} sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography
              variant={isMobile ? 'h6' : 'h4'}
              color='secondary'
              sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}
            >
              {title ? title : plur(model).replace(/([A-Z])/g, ' $1').trim()}
            </Typography>
          </Grid>

          <Grid container size={{ xs: 12, md: 8 }}>
            <Stack direction='row' spacing={1} alignItems='center' flex={1} justifyContent='flex-end'>
              <Stack sx={{ pr: 2 }}>
                <FilterMenu
                  model={model}
                  filters={listParams.getFilters()}
                  filtersInfo={optionsItemInfo}
                  onChangeFilter={onChangeFilterHandler}
                  onClearFilter={onClearFilterHandler}
                  disabledFilters={['paymentMethodId']}
                />
              </Stack>

              <Stack flex={1} direction='row' spacing={1} alignItems='center' sx={{ maxWidth: { xs: '100%', md: '400px' } }}>
                <SearchInput
                  value={localSearchKey}
                  onChange={onChangeLocalSearchKeyHandler}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onChangeSearchHandler();
                    }
                  }}
                  onClear={() => {
                    setLocalSearchKey('');
                    listParams.setSearch('');
                  }}
                />
                <Button
                  variant='contained'
                  size='small'
                  label={t('Search')}
                  onClick={onChangeSearchHandler}
                />
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        {
          tabs && (
            <Tabs
              value={currentTabIndex}
              onChange={onChangeTabHandler}
              variant="scrollable"
              sx={{
                '& .MuiTabs-scrollButtons.Mui-disabled': {
                  opacity: 0.2,
                },
              }}
            >
              {tabs.map(renderTab)}
            </Tabs>
          )
        }

        {
          get(itemsInfo, 'data.items') && (
            <Table
              ref={tableRef}
              height={tableHeight}
              modelName={model}
              params={listParams}
              info={itemsInfo}
              filtersInfo={filtersInfo}
              fetchData={fetchData}
              headCells={headCells}
              renderRow={renderRow}
              dispatch={dispatch}
              onCreate={permissions.create ? onClickCreateHandler : undefined}
              onClickView={onClickViewHandler}
              onClickEdit={permissions.update ? onClickEditHandler : undefined}
              onClickDelete={permissions.delete ? onClickDeleteHandler : undefined}
              onClickUnsubscribe={onClickUnsubscribeHandler}
              hideToolbar
            />
          )
        }
      </Stack>

      <DialogView
        open={dialogViewId !== false}
        onClose={onCloseDialogViewHandler}
        model={model}
        id={dialogViewId}
        payload={payload}
      />
      {
        (!hasDedicatedPages && !onCreate && !onEdit) && (
          <DialogEdit
            open={dialogEditId !== false}
            onClose={onCloseDialogEditHandler}
            model={model}
            id={dialogEditId}
            payload={payload}
          />
        )
      }
      <DialogDelete
        open={dialogDeleteId !== false}
        onClose={onCloseDialogDeleteHandler}
        model={model}
        id={dialogDeleteId}
        payload={payload}
      />
    </>
  );
}

GeneralListLayout.propTypes = {
  model: PropTypes.string.isRequired,
  tabs: PropTypes.array,
  headCells: PropTypes.array,
  payload: PropTypes.object,
  defaultListParams: PropTypes.object,
  title: PropTypes.string,
  fullHeight: PropTypes.bool,
  forceReload: PropTypes.bool,
  onCreate: PropTypes.func,
  onEdit: PropTypes.func,
  onFetching: PropTypes.func,
  onView: PropTypes.func,
};

export default GeneralListLayout;
